import { ReactFlowProvider } from '@xyflow/react';
import Sidebar from '../Components/_dataset/Sidebar';
import { Box } from '@mui/material';
import DatasetFilter from '../Components/DataSet/DatasetFilter';
import Canvas from '../Components/_dataset/Canvas';
import { useEffect, useMemo, useState } from 'react';
import {
  IFilter,
  relationshipServerObjProps,
} from '../Components/DataSet/types/BottomBarInterfaces';
import Actions from '../Components/_dataset/Actions';
import { PopUpSpinner } from '../Components/CommonFunctions/DialogComponents';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDataset } from '../hooks/useDataset';
import { useDispatch, useSelector } from 'react-redux';
import { IArrow, IConnection, ISelectedTableWithWorkspaces } from '../redux/_Dataset/types';
import { colors } from '../Components/ChartOptions/Color/ColorScheme';
import { LoadState } from '../redux/_Dataset/dataset.action';
import { TDatabase } from '../types/types';
import {
  fetchCustomQuery,
  getFileDataColumnDetails,
  getMetaDataColumns,
  getMetaDataColumnsForMySQL,
  getWebApiDataColumnDetails,
} from '../api';
import ShortUniqueId from 'short-unique-id';
import { FindShowHeadAndShowTail } from '../Components/CommonFunctions/FindIntegrityAndCordinality';
import { permissions } from '../Components/CommonFunctions/aliases';
import { Helmet } from 'react-helmet-async';
import { validateWebApiTable } from '../Components/_dataset/webApiValidation';
import { RootState } from '../redux';
import { getWebApiResponse } from '../api/webApi';

function EditDataset() {
  const { datasetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const uid = new ShortUniqueId({ length: 4, dictionary: 'alpha' });
  const uid_arrow = new ShortUniqueId();
  const { result, loading: apiLoading, error } = useDataset(datasetId!, location.state.parentId!);

  //  Get the current tables and active connection
  const selectedTables = useSelector((state: RootState) => state.dataset.selectedTables);
  const selectedConnectionId = useSelector(
    (state: RootState) => state.dataset.selectedConnectionId,
  );
  const connections = useSelector((state: RootState) => state.dataset.connections);

  const selectedConnection = useMemo(() => {
    return connections.find((c) => c.id === selectedConnectionId);
  }, [connections, selectedConnectionId]);

  /**all filters applied will be stored in the array */
  const [dataSetFilterArray, setDataSetFilterArray] = useState<IFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDataSetVisible, setIsDataSetVisible] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(15);
  const [showSidebar, setShowSidebar] = useState(true);
  const [singleSidebar, setSingleSidebar] = useState(false);

  // WEB API VALIDATION STATE

  const [responseData, setResponseData] = useState<any>(null);
  const [webApiLoading, setWebApiLoading] = useState(false);

  // fetch Web API response when connection changes
  useEffect(() => {
    if (!selectedConnection || selectedConnection.fileType !== 'webapi') return;

    const fetchResponse = async () => {
      setWebApiLoading(true);
      const res = await getWebApiResponse(selectedConnection.id);
      if (!res.isError) {
        setResponseData(res.data);
      }
      setWebApiLoading(false);
    };

    fetchResponse();
  }, [selectedConnection?.id]);

  const invalidTables = useMemo(() => {
    const invalid = new Set<string>();

    if (!responseData || !selectedConnection || webApiLoading) return invalid;

    selectedTables.forEach((table) => {
      if (table.isWebApi && !table.isNewTable && table.connectionId === selectedConnection.id) {
        const isValid = validateWebApiTable(table, responseData);
        if (!isValid) {
          invalid.add(table.id);
        }
      }
    });

    return invalid;
  }, [selectedTables, responseData, selectedConnection?.id, webApiLoading]);

  const onConnectionDelete = (connectionId: string) => {
    setDataSetFilterArray(
      dataSetFilterArray.filter((filter) => filter.connectionId !== connectionId),
    );
  };
  const onTableUncheck = (tableIds: string[]) => {
    setDataSetFilterArray(
      dataSetFilterArray.filter((filter) => !tableIds.includes(filter.tableId)),
    );
  };
  async function setDataset(result: any) {
    let tables: ISelectedTableWithWorkspaces[] = result.dataSchema.tables;
    let filterPanels = result.dataSchema.filterPanels;
    let relationships: relationshipServerObjProps[] = result.dataSchema.relationships;
    /**
     *
     * columnsmap={
     * [tableId]:litsOfColumns
     * }
     */
    let tableMap: {
      [tableId: string]: {
        database: string;
        schema?: string;
      };
    } = {};
    let columnsmap: {
      [key: string]: {
        uid: any;
        columnName: string;
        dataType: string;
      }[];
    } = {};
    const connectionMap: Record<string, IConnection> = {};
    const databaseMap: Record<string, string> = {};
    const schemaMap: Record<string, string> = {};
    let idx = 0;
    let tableOrder = 1;
    for (const table of tables) {
      // table.is;
      table.order = tableOrder++;
      const key = table.isFlatFile ? table.flatFileId : table.connectionId;
      if (!connectionMap[key]) {
        connectionMap[key] = {
          id: key,
          label: table.connectionName,
          workSpaceId: '',
          fileType: table.isFlatFile ? 'flatfile' : table.vendor === 'webapi' ? 'webapi' : 'dbcon',
          vendor: table.isFlatFile ? undefined : (table.vendor as TDatabase),
          color: colors[idx++ % colors.length],
          isNew: false,
          workspaces: table.workspaces || [],
        };
      }
      if (!table.isFlatFile && !databaseMap[key] && !table.isCustomQuery)
        databaseMap[key] = table.database;
      if (!table.isFlatFile && !schemaMap[key] && !table.isCustomQuery)
        schemaMap[key] = table.schema;
      if (!table.isCustomQuery && !tableMap[table.id]) {
        tableMap[table.id] = { database: table.database, schema: table.schema };
      }
    }
    const connections: IConnection[] = Object.values(connectionMap);

    // dispatch(UpdateConnections(connections));
    try {
      tables = await Promise.all(
        tables.map(async (table) => {
          if (table.isFlatFile) {
            const res = await getFileDataColumnDetails({
              flatFileId: table.flatFileId,
              workspaceId: '',
            });
            if (res.isError) throw new Error('API Error');
            const columns = res.data!.map((column: { fieldName: string; dataType: string }) => ({
              uid: uid(),
              columnName: column.fieldName,
              dataType: column.dataType,
            }));
            columnsmap[table.id] = columns;
            const { workspaces, ...rest } = table;
            return {
              ...rest,
              isNewTable: false,
              isSelected: true,
              columns,
            };
            // optionally do something with res
          } else {
            if (table.vendor === 'webapi') {
              const res = await getWebApiDataColumnDetails({
                tableId: table.id,
                datasetId: datasetId ?? result.id,
                workspaceId: '',
              });
              if (res.isError) throw new Error('API Error');
              let columns = table.columns ?? [];
              columns = res.data!.map((column: any) => ({
                uid: uid(),
                columnName: column.fieldName ?? column.columnName,
                dataType: column.dataType,
              }));
              columnsmap[table.id] = columns;
              return {
                ...table,
                columns,
                isSelected: true,
                isNewTable: false,
                isWebApi: true,
              };
            } else {
              const schemaNeeded = !['mysql', 'mariadb', 'clickhouse'].includes(table.vendor!);
              const res = table.isCustomQuery
                ? await fetchCustomQuery({
                    databaseId: table.connectionId ?? result.connectionId,
                    workspaceId: '',
                    queryData: table.customQuery,
                  })
                : schemaNeeded
                  ? await getMetaDataColumns({
                      databaseId: table.connectionId ?? result.connectionId,
                      databaseName: table.database,
                      workspaceId: '',
                      tableName: table.table,
                      schemaName: table.schema,
                    })
                  : await getMetaDataColumnsForMySQL({
                      databaseId: table.connectionId ?? result.connectionId,
                      databaseName: table.database,
                      workspaceId: '',
                      tableName: table.table,
                    });
              if (res.isError) throw new Error('API Error');
              const columns = res.data!.map((column) => ({
                uid: uid(),
                ...column,
              }));
              columnsmap[table.id] = columns;
              return {
                ...table,
                columns,
                isSelected: true,
                isNewTable: false,
              };
            }
          }
        }),
      );
    } catch (e: any) {
      navigate('/');
      return;
    }
    const arrows: IArrow[] = [];
    const id = new ShortUniqueId({ length: 6 });
    relationships.forEach((relation) => {
      const arrowDirs = FindShowHeadAndShowTail(relation.cardinality);

      const startTableColumns = columnsmap[relation.table1];
      const endTableColumns = columnsmap[relation.table2];
      const startTable = tables.find((table) => table.id === relation.table1);
      const endTable = tables.find((table) => table.id === relation.table2);
      const relationId = id();
      if (!startTable || !endTable) {
        return;
      }
      // const rel = {
      //   integrity: relation.refIntegrity,
      //   cardinality: relation.cardinality,
      //   showHead: arrowDirs?.showHead,
      //   showTail: arrowDirs?.showTail,
      //   relationId: relationId,
      //   startTableName: startTable.table,
      //   endTableName: endTable.table,
      //   startId: startTable.id,
      //   endId: endTable.id,
      // };
      relation.table1Columns.forEach((column, idx) => {
        const startColumn = startTableColumns.find((_column) => _column.columnName === column);
        const endColumn = endTableColumns.find(
          (_column) => _column.columnName === relation.table2Columns[idx],
        );
        if (!startColumn || !endColumn) return;
        const arrow: IArrow = {
          arrowId: uid_arrow(),
          cardinality: relation.cardinality,
          end: endColumn.uid,
          start: startColumn.uid,
          endSchema: endTable.schema,
          startSchema: startTable.schema,
          endTableName: endTable.table,
          startTableName: startTable.table,
          isSelected: false,
          relationId,
          showHead: arrowDirs.showHead,
          showTail: arrowDirs.showTail,
          startColumnName: startColumn.columnName,
          endColumnName: endColumn.columnName,
          startId: startTable.id,
          endId: endTable.id,
          integrity: relation.refIntegrity,
          table1_uid: startTable.id,
          table2_uid: endTable.id,
          isExisting: true,
        };
        arrows.push(arrow);
      });
    });
    dispatch(
      LoadState({
        arrows,
        datasetId: result.id,
        tables: [],
        views: [],
        datasetName: result.datasetName,
        connections,
        selectedTables: tables,
        selectedConnectionId: connections[0].id,
        database: databaseMap,
        schema: schemaMap,
        isExtract: result.isExtract ?? false,
        canEdit: !(
          result.privilege === permissions.view || result.privilege === permissions.restricted
        ),
      }),
    );
    if (filterPanels.length > 0) {
      const filters: IFilter[] = filterPanels[0].filters
        .map((filter: any) => {
          const { isCalculatedField, currentSelection, conditionType, ...rest } = filter;
          const tableInfo = tableMap[filter.tableId];
          if (!tableInfo) return null;

          return { ...rest, database: tableInfo.database, schema: tableInfo.schema } as IFilter;
        })
        .filter((filter: IFilter | null) => filter !== null);

      setDataSetFilterArray(filters);
    }
    setLoading(false);
    // dispatch(SetDatasetName(result.datasetName));
    // dispatch(SetSelectedTables(tables));
  }

  useEffect(() => {
    if (!result) return;
    setDataset(result);
  }, [result]);

  if (loading || apiLoading) {
    return (
      <PopUpSpinner
        show={loading || apiLoading}
        paperProps={{
          sx: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      />
    );
  }
  return (
    <>
      <Helmet prioritizeSeoTags defer>
        <title>{`${result?.datasetName ? `${result.datasetName} - Dataset | Silzila` : 'Dataset | Silzila'}`}</title>
      </Helmet>
      <ReactFlowProvider>
        <Box
          key={location.pathname}
          sx={{
            display: 'flex',
            height: '100%',
          }}
        >
          <Sidebar
            onConnectionDelete={onConnectionDelete}
            onTableUncheck={onTableUncheck}
            onSidebarWidthChange={setSidebarWidth}
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            setSingleSidebar={setSingleSidebar}
          />

          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                height: 'calc(100% - 3rem)',
                position: 'relative',
                flexGrow: 1,
                '& .react-flow__minimap': {
                  right: isDataSetVisible ? '100px !important' : '40px !important',
                },
              }}
            >
              <Canvas
                key="editdataset"
                onTableUncheck={onTableUncheck}
                datasetFilterWidth={isDataSetVisible ? 13.4 : 2.7}
                sidebarWidth={sidebarWidth}
                showSidebar={showSidebar}
                singleSidebar={singleSidebar}
                invalidTables={invalidTables}
              />
              <DatasetFilter
                editMode={true}
                dataSetFilterArray={dataSetFilterArray}
                setDataSetFilterArray={setDataSetFilterArray}
                isDataSetVisible={isDataSetVisible}
                setIsDataSetVisible={setIsDataSetVisible}
              />
            </Box>
            <Actions datasetFilters={dataSetFilterArray} editMode invalidTables={invalidTables} />
          </Box>
        </Box>
      </ReactFlowProvider>
    </>
  );
}

export default EditDataset;
